import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState, useEffect } from 'react'
import { X, Play, AlertCircle, ArrowRight, History } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { sportsService } from './sportsService'
import { fieldCaptureService } from './fieldCaptureService'
import {
  PracticeScript,
  LineupTemplate,
  SnapMode,
  RosterAthleteOption,
} from '../../types/sports'
import { TeamBlockCapture, FieldSnap } from '../../types/fieldCapture'
import { SnapHistoryModal } from './SnapHistoryModal'
import { PastSnapDetailModal } from './PastSnapDetailModal'
import { supabase } from '../../lib/supabase'

interface FieldCaptureEntryModalProps {
  isOpen: boolean
  onClose: () => void
  teamId: string
  blockId?: string | null
  sessionId?: string | null
  sessionTitle?: string | null
  blockTitle?: string | null
  initialScriptId?: string | null
  onLaunchCapture: (capture: TeamBlockCapture) => void
}

export const FieldCaptureEntryModal: React.FC<FieldCaptureEntryModalProps> = ({
  isOpen,
  onClose,
  teamId,
  blockId,
  sessionId,
  sessionTitle,
  blockTitle,
  initialScriptId,
  onLaunchCapture,
}) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Captura existente detectada
  const [existingCapture, setExistingCapture] = useState<TeamBlockCapture | null>(null)
  const [existingSnapCount, setExistingSnapCount] = useState<number>(0)

  // Capturas completadas previas (Ajuste M4 - Requerimiento 3)
  const [completedCaptures, setCompletedCaptures] = useState<TeamBlockCapture[]>([])
  const [selectedHistoryCapture, setSelectedHistoryCapture] = useState<TeamBlockCapture | null>(null)

  useModalLifecycle(isOpen, onClose)
  const [historySnaps, setHistorySnaps] = useState<FieldSnap[]>([])
  const [historyModalOpen, setHistoryModalOpen] = useState(false)
  const [selectedDetailSnap, setSelectedDetailSnap] = useState<FieldSnap | null>(null)
  const [pastSnapDetailOpen, setPastSnapDetailOpen] = useState(false)
  const [rosterAthletes, setRosterAthletes] = useState<RosterAthleteOption[]>([])

  // Catálogos
  const [scripts, setScripts] = useState<PracticeScript[]>([])
  const [lineupTemplates, setLineupTemplates] = useState<LineupTemplate[]>([])

  // Configuración de nueva captura
  const [mode, setMode] = useState<SnapMode>('guided_practice')
  const [sourceType, setSourceType] = useState<'script' | 'free'>('script')
  const [selectedScriptId, setSelectedScriptId] = useState<string>('')
  const [selectedOffenseTmplId, setSelectedOffenseTmplId] = useState<string>('')
  const [selectedDefenseTmplId, setSelectedDefenseTmplId] = useState<string>('')
  const [rivalName, setRivalName] = useState<string>('Rival Competición')
  const [captureTitle, setCaptureTitle] = useState<string>('')

  // Carga inicial al abrir
  useEffect(() => {
    if (!isOpen) return

    let isMounted = true
    const loadEntryData = async () => {
      setLoading(true)
      setErrorMsg(null)
      try {
        // 1. Cargar guiones, plantillas y atletas
        const [loadedScripts, loadedLineups, loadedAthletes] = await Promise.all([
          sportsService.getPracticeScripts(teamId),
          sportsService.getLineupTemplates(teamId, { status: 'active' }),
          sportsService.getRosterAthleteOptions(teamId),
        ])

        if (!isMounted) return
        setScripts(loadedScripts)
        setLineupTemplates(loadedLineups)
        setRosterAthletes(loadedAthletes)

        // Preseleccionar script si viene dado o tomar el primero
        const targetScriptId =
          initialScriptId ||
          (loadedScripts.find((s) => s.block_id === blockId)?.id) ||
          (loadedScripts.length > 0 ? loadedScripts[0].id : '')

        setSelectedScriptId(targetScriptId)
        if (!targetScriptId) {
          setSourceType('free')
        }

        // Preseleccionar plantillas
        const off = loadedLineups.find((l) => l.unit === 'offense')
        const def = loadedLineups.find((l) => l.unit === 'defense')
        if (off) setSelectedOffenseTmplId(off.id)
        if (def) setSelectedDefenseTmplId(def.id)

        setCaptureTitle(
          blockTitle
            ? `Captura: ${blockTitle}`
            : sessionTitle
            ? `Captura: ${sessionTitle}`
            : 'Captura de bloque Team'
        )

        // 2. Buscar captura activa existente
        if (supabase) {
          let query = supabase
            .from('team_block_captures')
            .select('*')
            .eq('team_id', teamId)
            .in('status', ['active', 'paused'])

          if (blockId) {
            query = query.eq('block_id', blockId)
          }

          const { data: existingList } = await query
            .order('created_at', { ascending: false })
            .limit(1)

          if (isMounted && existingList && existingList.length > 0) {
            const cap = existingList[0] as TeamBlockCapture
            setExistingCapture(cap)

            // Contar snaps registrados
            const { count } = await supabase
              .from('snaps')
              .select('*', { count: 'exact', head: true })
              .eq('capture_id', cap.id)

            if (isMounted) {
              setExistingSnapCount(count || 0)
            }
          } else {
            setExistingCapture(null)
          }

          // 3. Buscar capturas completadas (Ajuste M4 - Requerimiento 3)
          let compQuery = supabase
            .from('team_block_captures')
            .select('*')
            .eq('team_id', teamId)
            .eq('status', 'completed')

          if (sessionId) {
            compQuery = compQuery.eq('session_id', sessionId)
          }

          const { data: compList } = await compQuery
            .order('ended_at', { ascending: false })
            .limit(6)

          if (isMounted && compList) {
            setCompletedCaptures(compList as TeamBlockCapture[])
          }
        }
      } catch (err: unknown) {
        if (isMounted) {
          setErrorMsg(err instanceof Error ? err.message : 'Error al cargar opciones de captura.')
        }
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadEntryData()
    return () => {
      isMounted = false
    }
  }, [isOpen, teamId, blockId, sessionId, initialScriptId, blockTitle, sessionTitle])

  if (!isOpen) return null

  const handleContinueExisting = () => {
    if (!existingCapture) return
    onLaunchCapture(existingCapture)
    onClose()
  }

  const handleInspectHistory = async (cap: TeamBlockCapture) => {
    setLoading(true)
    try {
      const details = await fieldCaptureService.getCaptureDetails(cap.id)
      setSelectedHistoryCapture(details.capture)
      setHistorySnaps(details.snaps)
      setHistoryModalOpen(true)
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar historial de jugadas.')
    } finally {
      setLoading(false)
    }
  }

  const handleStartNewCapture = async () => {
    if (!user) return
    setLoading(true)
    setErrorMsg(null)
    try {
      const deviceId =
        localStorage.getItem('team_spain_device_id') ||
        (() => {
          const id = 'dev-' + crypto.randomUUID().slice(0, 8)
          localStorage.setItem('team_spain_device_id', id)
          return id
        })()

      const capture = await fieldCaptureService.getOrCreateCaptureForBlock({
        teamId,
        blockId: blockId || null,
        sessionId: sessionId || null,
        scriptId: mode !== 'match' && sourceType === 'script' ? selectedScriptId || null : null,
        title: captureTitle.trim() || 'Captura en Campo',
        mode,
        deviceId,
        userId: user.id,
      })

      // Si es modo partido, inicializar match_context
      if (mode === 'match') {
        await fieldCaptureService.updateMatchContext(capture.id, {
          rival_name: rivalName.trim() || 'Rival Competición',
          our_score: 0,
          rival_score: 0,
          period: '1ª Parte',
          possession_side: 'offense',
          clock_seconds: 1200, // 20 minutos por parte estándar IFAF
        })
      }

      onLaunchCapture(capture)
      onClose()
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al iniciar la sesión de captura.')
    } finally {
      setLoading(false)
    }
  }

  const selectedOffenseTmpl = lineupTemplates.find((l) => l.id === selectedOffenseTmplId)
  const selectedDefenseTmpl = lineupTemplates.find((l) => l.id === selectedDefenseTmplId)

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '1rem',
        backdropFilter: 'blur(3px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '560px',
          backgroundColor: 'var(--surface-card)',
          borderRadius: '14px',
          padding: '1.25rem',
          boxShadow: 'var(--shadow-card)',
          maxHeight: '92vh',
          overflowY: 'auto',
          border: '1px solid var(--surface-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            borderBottom: '1px solid var(--surface-border)',
            paddingBottom: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                backgroundColor: 'var(--brand-red)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <Play size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                Consola Móvil de Campo
              </h3>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Registro ágil de snaps, eventos, sustituciones y referencias de vídeo
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '0.4rem',
            }}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {errorMsg && (
          <div
            style={{
              backgroundColor: 'var(--brand-red-subtle)',
              border: '1px solid var(--brand-red)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.6rem 0.85rem',
              color: 'var(--brand-red)',
              fontSize: '0.8rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AlertCircle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* ALERTA DE CAPTURA ACTIVA PREVIA */}
        {existingCapture && (
          <div
            style={{
              backgroundColor: 'var(--status-success-bg)',
              border: '1.5px solid var(--status-success-text)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.85rem',
              marginBottom: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: 'var(--status-success-text)',
                  textTransform: 'uppercase',
                }}
              >
                Captura en curso detectada
              </span>
              <span
                className="badge badge-success"
                style={{ fontSize: '0.7rem', fontVariantNumeric: 'tabular-nums' }}
              >
                {existingSnapCount} snaps registrados
              </span>
            </div>

            <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-main)', marginTop: '0.25rem' }}>
              {existingCapture.title}
            </div>

            <div
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                marginTop: '0.2rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Iniciada el {new Date(existingCapture.started_at).toLocaleDateString()} a las{' '}
              {new Date(existingCapture.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {' · '}
              Unidades: {existingCapture.offense_lineup_name} vs {existingCapture.defense_lineup_name}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleContinueExisting}
                style={{
                  fontSize: '0.82rem',
                  minHeight: '44px',
                  flex: 1,
                  backgroundColor: 'var(--brand-red)',
                }}
              >
                <ArrowRight size={16} />
                <span>Continuar esta captura existente</span>
              </button>
            </div>
          </div>
        )}

        {/* SESIONES / PARTIDOS COMPLETADOS PREVIOS (Ajuste M4 - Requerimiento 3) */}
        {completedCaptures.length > 0 && (
          <div
            style={{
              backgroundColor: 'var(--surface-subtle)',
              border: '1px solid var(--surface-border)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.75rem',
              marginBottom: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
              <span style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                Partidos y Bloques Finalizados ({completedCaptures.length})
              </span>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                Revisión cronológica de jugadas
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {completedCaptures.map((comp: TeamBlockCapture) => (
                <div
                  key={comp.id}
                  style={{
                    backgroundColor: 'var(--surface-card)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: '6px',
                    padding: '0.55rem 0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <strong style={{ fontSize: '0.82rem', color: 'var(--text-main)' }}>
                        {comp.title}
                      </strong>
                      <span className="badge badge-neutral" style={{ fontSize: '0.65rem' }}>
                        {comp.mode === 'match' ? 'Partido' : 'Entrenamiento'}
                      </span>
                      {comp.mode === 'match' && comp.match_context && (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--brand-red)' }}>
                          {comp.match_context.our_score} - {comp.match_context.rival_score} vs {comp.match_context.rival_name || 'Rival'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                      Finalizado el {new Date(comp.ended_at || comp.started_at).toLocaleDateString()} a las {new Date(comp.ended_at || comp.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => handleInspectHistory(comp)}
                      style={{ fontSize: '0.72rem', padding: '0.3rem 0.55rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                      title="Ver jugadas snap a snap sin iniciar captura"
                    >
                      <History size={12} />
                      <span>Ver Snaps</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        onLaunchCapture(comp)
                        onClose()
                      }}
                      style={{ fontSize: '0.72rem', padding: '0.3rem 0.55rem' }}
                      title="Abrir en consola"
                    >
                      Abrir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FORMULARIO COMPACTO DE NUEVA CAPTURA */}
        <div style={{ opacity: existingCapture ? 0.9 : 1 }}>
          <div
            style={{
              fontSize: '0.78rem',
              fontWeight: 700,
              color: 'var(--text-secondary)',
              marginBottom: '0.6rem',
              textTransform: 'uppercase',
            }}
          >
            {existingCapture ? 'O configurar un nuevo bloque de captura:' : 'Configuración de Captura:'}
          </div>

          {/* Modalidad de captura */}
          <div style={{ marginBottom: '0.85rem' }}>
            <label className="form-label" style={{ fontSize: '0.76rem', marginBottom: '0.35rem' }}>
              Modalidad de Bloque
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {(
                [
                  { id: 'guided_practice', label: 'Práctica guiada' },
                  { id: 'competitive_team', label: 'Team competitivo' },
                  { id: 'match', label: 'Partido / Amistoso' },
                ] as { id: SnapMode; label: string }[]
              ).map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => {
                    setMode(m.id)
                    if (m.id === 'match') setSourceType('free')
                  }}
                  style={{
                    padding: '0.55rem 0.4rem',
                    fontSize: '0.78rem',
                    fontWeight: mode === m.id ? 700 : 500,
                    borderRadius: 'var(--radius-sm)',
                    border: mode === m.id ? '2px solid var(--brand-red)' : '1px solid var(--surface-border)',
                    backgroundColor: mode === m.id ? 'var(--brand-red-subtle)' : 'var(--surface-card)',
                    color: mode === m.id ? 'var(--brand-red)' : 'var(--text-main)',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Origen: Con guion o libre */}
          {mode !== 'match' && (
            <div style={{ marginBottom: '0.85rem' }}>
              <label className="form-label" style={{ fontSize: '0.76rem', marginBottom: '0.35rem' }}>
                Origen del bloque
              </label>
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button
                  type="button"
                  onClick={() => setSourceType('script')}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    fontSize: '0.78rem',
                    fontWeight: sourceType === 'script' ? 700 : 500,
                    borderRadius: 'var(--radius-sm)',
                    border: sourceType === 'script' ? '1.5px solid var(--brand-red)' : '1px solid var(--surface-border)',
                    backgroundColor: sourceType === 'script' ? 'var(--surface-subtle)' : 'var(--surface-card)',
                    color: sourceType === 'script' ? 'var(--brand-red)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  Desde guion M3 ({scripts.length} disponibles)
                </button>
                <button
                  type="button"
                  onClick={() => setSourceType('free')}
                  style={{
                    flex: 1,
                    padding: '0.5rem',
                    fontSize: '0.78rem',
                    fontWeight: sourceType === 'free' ? 700 : 500,
                    borderRadius: 'var(--radius-sm)',
                    border: sourceType === 'free' ? '1.5px solid var(--brand-red)' : '1px solid var(--surface-border)',
                    backgroundColor: sourceType === 'free' ? 'var(--surface-subtle)' : 'var(--surface-card)',
                    color: sourceType === 'free' ? 'var(--brand-red)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  Bloque libre (Sin guion)
                </button>
              </div>

              {sourceType === 'script' && (
                <div style={{ marginTop: '0.5rem' }}>
                  <select
                    className="form-input"
                    value={selectedScriptId}
                    onChange={(e) => setSelectedScriptId(e.target.value)}
                    style={{ fontSize: '0.82rem' }}
                  >
                    {scripts.length === 0 ? (
                      <option value="">No hay guiones activos (iniciar libre)</option>
                    ) : (
                      scripts.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} ({s.reps_count || s.reps?.length || 0} repeticiones previstas)
                        </option>
                      ))
                    )}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Nombre de rival si es partido */}
          {mode === 'match' && (
            <div style={{ marginBottom: '0.85rem' }}>
              <label className="form-label" style={{ fontSize: '0.76rem' }}>
                Equipo Rival
              </label>
              <input
                type="text"
                value={rivalName}
                onChange={(e) => setRivalName(e.target.value)}
                placeholder="Ej. Selección de Alemania"
                className="form-input"
                style={{ fontSize: '0.82rem' }}
              />
            </div>
          )}

          {/* Quintetos iniciales */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.75rem',
              backgroundColor: 'var(--surface-subtle)',
              padding: '0.75rem',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--surface-border)',
              marginBottom: '1rem',
            }}
          >
            <div>
              <label className="form-label" style={{ fontSize: '0.74rem', color: 'var(--brand-red)' }}>
                Quinteto Ataque Inicial
              </label>
              <select
                className="form-input"
                value={selectedOffenseTmplId}
                onChange={(e) => setSelectedOffenseTmplId(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              >
                {lineupTemplates
                  .filter((l) => l.unit === 'offense')
                  .map((tmpl) => (
                    <option key={tmpl.id} value={tmpl.id}>
                      {tmpl.name}
                    </option>
                  ))}
              </select>
              {selectedOffenseTmpl && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  5 jugadores: {selectedOffenseTmpl.slots.map((s) => s.position).join(', ')}
                </div>
              )}
            </div>

            <div>
              <label className="form-label" style={{ fontSize: '0.74rem', color: 'var(--nav-bg-surface)' }}>
                Quinteto Defensa Inicial
              </label>
              <select
                className="form-input"
                value={selectedDefenseTmplId}
                onChange={(e) => setSelectedDefenseTmplId(e.target.value)}
                style={{ fontSize: '0.8rem' }}
              >
                {lineupTemplates
                  .filter((l) => l.unit === 'defense')
                  .map((tmpl) => (
                    <option key={tmpl.id} value={tmpl.id}>
                      {tmpl.name}
                    </option>
                  ))}
              </select>
              {selectedDefenseTmpl && (
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  5 jugadores: {selectedDefenseTmpl.slots.map((s) => s.position).join(', ')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--surface-border)',
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{ fontSize: '0.82rem', minHeight: '44px' }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={handleStartNewCapture}
            style={{
              fontSize: '0.82rem',
              minHeight: '44px',
              backgroundColor: 'var(--brand-red)',
            }}
          >
            {loading ? 'Iniciando captura...' : 'Comenzar captura en campo'}
          </button>
        </div>
      </div>

      {/* MODAL DE HISTORIAL CRONOLÓGICO (Ajuste M4 - Requerimiento 3) */}
      <SnapHistoryModal
        isOpen={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false)
          setSelectedHistoryCapture(null)
          setHistorySnaps([])
        }}
        capture={selectedHistoryCapture}
        snaps={historySnaps}
        onSelectSnap={(snap) => {
          setSelectedDetailSnap(snap)
          setPastSnapDetailOpen(true)
        }}
        rosterAthletes={rosterAthletes}
      />

      {/* MODAL DE DETALLE Y AUDITORÍA DE SNAP PASADO */}
      <PastSnapDetailModal
        isOpen={pastSnapDetailOpen}
        onClose={() => {
          setPastSnapDetailOpen(false)
          setSelectedDetailSnap(null)
        }}
        snap={selectedDetailSnap}
        onCorrectResult={async (params) => {
          const updated = await fieldCaptureService.correctSnapResult({
            snapId: params.snapId,
            newPrimaryResult: params.newPrimaryResult,
            passerAthleteId: params.passerId,
            receiverAthleteId: params.receiverId,
            tacklerAthleteId: params.tacklerId,
          })
          setHistorySnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
          setSelectedDetailSnap(updated)
        }}
        onVoidSnap={async (snapId, validity, reason) => {
          await fieldCaptureService.voidSnap(snapId, validity, reason)
          setHistorySnaps((prev) =>
            prev.map((s) => (s.id === snapId ? { ...s, validity, review_notes: reason } : s))
          )
        }}
        onUpdateSnapDetails={async (params) => {
          const updated = await fieldCaptureService.updateSnapDetails(params)
          setHistorySnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
          setSelectedDetailSnap(updated)
        }}
        rosterAthletes={rosterAthletes}
      />
    </div>
  )
}
